import {
  Activity,
  AlarmClock,
  CalendarCheck,
  ClipboardCheck,
  FileClock,
  Package,
  PalmtreeIcon,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";

import type {
  IWidgetDefinition,
  IWidgetPlacement,
} from "@/components/dashboard-grid";
import { PATHS } from "@/routes/paths";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { ActivityWidget } from "./ActivityWidget";
import { AssetRequestsWidget } from "./AssetRequestsWidget";
import { AttendanceTodayWidget } from "./AttendanceTodayWidget";
import { HeadcountWidget } from "./HeadcountWidget";
import { LeaveRequestsWidget } from "./LeaveRequestsWidget";
import { NewHiresWidget } from "./NewHiresWidget";
import { OnLeaveTodayWidget } from "./OnLeaveTodayWidget";
import { PendingApprovalsWidget } from "./PendingApprovalsWidget";
import { QuickActionsWidget } from "./QuickActionsWidget";
import { RegularizationWidget } from "./RegularizationWidget";

/**
 * The Workspace dashboard's widget catalogue.
 *
 * A definition owns everything *around* the data — name, icon, tone, deep link,
 * allowed sizes, permission — and the widget component renders only its body.
 * `component` is a reference, not a render callback: React reconciles it by type,
 * each widget memoises independently, and a crash inside one lands in that card's
 * error boundary instead of unwinding the whole canvas.
 * One header implementation for the whole canvas is what keeps ten widgets at
 * up to five sizes reading as one dashboard instead of ten separate cards.
 *
 * `xs` is only on the six widgets whose entire body is one number: headcount,
 * pending approvals, regularization, on leave today, leave requests, asset
 * requests. Attendance today needs room for its ring, activity and new hires
 * need room for a face, quick actions needs room for more than one tile — none
 * of them have anything smaller to say at a sixth of a row, so it is left off
 * their `sizes` rather than given a cramped, half-working layout.
 *
 * `tall` and `grand` are the same kind of opt-in from the other axis. `tall`
 * (`sm`'s width, twice the height) goes on headcount, regularization, leave
 * requests, asset requests, quick actions and attendance today — the widgets
 * whose `md` layout puts a second row of context *beside* the number; `tall`
 * gives that same context a column beneath it instead, so growing a card no
 * longer means growing it sideways. `grand` (`xl`'s width, a third row of
 * height) goes on attendance today, activity, pending approvals, on leave
 * today and new hires — the list- and chart-shaped widgets, where the extra
 * row buys more rows or a taller chart rather than more columns.
 *
 * Every widget is a singleton: two copies of "Attendance today" would show the
 * same number twice, and the duplicate would read as a rendering bug rather than
 * a choice. A future widget that is genuinely parameterisable (a
 * department-scoped headcount, say) is where `singleton: false` earns its place.
 *
 * `section` is the permission gate. It filters the library, and it filters an
 * already-saved layout at render time — so a role change hides the widget
 * without rewriting the layout, and restoring the permission brings it back.
 */
export const WORKSPACE_WIDGETS: IWidgetDefinition[] = [
  {
    type: "headcount",
    name: "company:widgets.active_employees",
    description: "company:widgets.headcount_desc",
    icon: Users,
    tone: "info",
    category: "company:widgets.cat_people",
    sizes: ["xs", "sm", "tall", "md", "lg"],
    defaultSize: "sm",
    section: PLATFORM_SECTION_ENUM.EMPLOYEES,
    link: {
      label: "company:widgets.link_all",
      to: PATHS.COMPANY.COMPANY_EMPLOYEES_PATHS.ACTIVE,
    },
    singleton: true,
    component: HeadcountWidget,
  },
  {
    type: "attendance-today",
    name: "company:widgets.attendance_today",
    description: "company:widgets.attendance_today_desc",
    icon: CalendarCheck,
    tone: "success",
    category: "company:widgets.cat_time_attendance",
    sizes: ["sm", "tall", "md", "lg", "xl", "grand"],
    defaultSize: "md",
    section: PLATFORM_SECTION_ENUM.TIME_ATTENDANCE,
    link: {
      label: "company:widgets.link_open",
      to: PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.OVERVIEW,
    },
    singleton: true,
    component: AttendanceTodayWidget,
  },
  {
    type: "pending-approvals",
    name: "company:widgets.pending_approvals",
    description: "company:widgets.pending_approvals_desc",
    icon: ClipboardCheck,
    tone: "warning",
    category: "company:widgets.cat_time_attendance",
    sizes: ["xs", "sm", "md", "lg", "xl", "grand"],
    defaultSize: "sm",
    section: PLATFORM_SECTION_ENUM.TIME_ATTENDANCE,
    link: {
      label: "company:widgets.link_review",
      to: PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.REGULARIZATION,
    },
    singleton: true,
    component: PendingApprovalsWidget,
  },
  {
    type: "regularization",
    name: "company:widgets.regularization",
    description: "company:widgets.regularization_desc",
    icon: AlarmClock,
    tone: "warning",
    category: "company:widgets.cat_time_attendance",
    sizes: ["xs", "sm", "tall", "md"],
    defaultSize: "sm",
    section: PLATFORM_SECTION_ENUM.TIME_ATTENDANCE,
    link: {
      label: "company:widgets.link_review",
      to: PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.REGULARIZATION,
    },
    singleton: true,
    component: RegularizationWidget,
  },
  {
    type: "activity",
    name: "company:widgets.attendance_activity",
    description: "company:widgets.attendance_activity_desc",
    icon: Activity,
    tone: "info",
    category: "company:widgets.cat_time_attendance",
    sizes: ["md", "lg", "xl", "grand"],
    defaultSize: "lg",
    section: PLATFORM_SECTION_ENUM.TIME_ATTENDANCE,
    link: {
      label: "company:widgets.link_all",
      to: PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.OVERVIEW,
    },
    singleton: true,
    component: ActivityWidget,
  },
  {
    type: "on-leave-today",
    name: "company:widgets.on_leave_today",
    description: "company:widgets.on_leave_today_desc",
    icon: PalmtreeIcon,
    tone: "info",
    category: "company:widgets.cat_leave",
    sizes: ["xs", "sm", "md", "lg", "xl", "grand"],
    defaultSize: "sm",
    section: PLATFORM_SECTION_ENUM.LEAVE_MANAGEMENT,
    link: {
      label: "company:widgets.all_leaves",
      to: PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.ALL_LEAVES,
    },
    singleton: true,
    component: OnLeaveTodayWidget,
  },
  {
    type: "leave-requests",
    name: "company:widgets.leave_requests",
    description: "company:widgets.leave_requests_desc",
    icon: FileClock,
    tone: "primary",
    category: "company:widgets.cat_leave",
    sizes: ["xs", "sm", "tall", "md", "lg"],
    defaultSize: "sm",
    section: PLATFORM_SECTION_ENUM.LEAVE_MANAGEMENT,
    link: {
      label: "company:widgets.link_open",
      to: PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.ALL_LEAVES,
    },
    singleton: true,
    component: LeaveRequestsWidget,
  },
  {
    type: "asset-requests",
    name: "company:widgets.asset_requests",
    description: "company:widgets.asset_requests_desc",
    icon: Package,
    tone: "primary",
    category: "company:widgets.cat_assets",
    sizes: ["xs", "sm", "tall", "md", "lg"],
    defaultSize: "sm",
    section: PLATFORM_SECTION_ENUM.ASSETS,
    link: {
      label: "company:widgets.link_open",
      to: PATHS.COMPANY.COMPANY_ASSETS_PATHS.ASSET_REQUESTS,
    },
    singleton: true,
    component: AssetRequestsWidget,
  },
  {
    type: "new-hires",
    name: "company:widgets.new_hires",
    description: "company:widgets.new_hires_desc",
    icon: UserPlus,
    tone: "success",
    category: "company:widgets.cat_people",
    sizes: ["sm", "md", "lg", "xl", "grand"],
    defaultSize: "md",
    section: PLATFORM_SECTION_ENUM.EMPLOYEES,
    link: {
      label: "company:widgets.link_all",
      to: PATHS.COMPANY.COMPANY_EMPLOYEES_PATHS.ALL,
    },
    singleton: true,
    component: NewHiresWidget,
  },
  {
    type: "quick-actions",
    name: "company:widgets.quick_actions",
    description: "company:widgets.quick_actions_desc",
    icon: Zap,
    tone: "primary",
    category: "company:widgets.cat_shortcuts",
    // Gated on Home rather than a module: the tiles inside carry their own
    // per-action permission checks, so the widget itself is always placeable.
    sizes: ["sm", "tall", "md", "lg"],
    defaultSize: "md",
    section: PLATFORM_SECTION_ENUM.HOME,
    singleton: true,
    component: QuickActionsWidget,
  },
];

/**
 * The out-of-the-box arrangement: two full rows of the 12-column grid, so a
 * first-time user sees a complete canvas rather than a ragged one.
 *
 * Row 1 — headcount (3) + attendance today (6) + pending approvals (3)
 * Row 2 — on leave (3) + leave requests (3) + regularization (3) + assets (3)
 *
 * Instance ids are fixed rather than generated: this list is a module constant,
 * so a random id would change on every evaluation and defeat React's keys. It is
 * also what the loading skeleton is shaped from. Widgets the user cannot see are
 * dropped from it at render time.
 */
export const DEFAULT_WORKSPACE_LAYOUT: IWidgetPlacement[] = [
  { instanceId: "default-headcount", type: "headcount", size: "sm" },
  { instanceId: "default-attendance", type: "attendance-today", size: "md" },
  { instanceId: "default-approvals", type: "pending-approvals", size: "sm" },
  { instanceId: "default-on-leave", type: "on-leave-today", size: "sm" },
  { instanceId: "default-leave-requests", type: "leave-requests", size: "sm" },
  { instanceId: "default-regularization", type: "regularization", size: "sm" },
  { instanceId: "default-asset-requests", type: "asset-requests", size: "sm" },
];
